import {
  FC,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  FilterOptionsState,
  FormHelperText,
  InputBaseProps,
  InputLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

import { IcArrowDown, IcClose, IcPlusCircle } from '@/assets/images';
import { NoResultItem } from '@/components';

import { AutoCompleteRoot, TextField } from './styles';
import { Chip } from '../../Chip';
import { BaseFieldProps } from '../types';

interface OptionType {
  inputValue?: string;
  label: string | ReactElement;
  value: string | number;
  group?: string;
  disabled?: boolean;
}
const filter = createFilterOptions<string | OptionType>();

const isSameOption = (a: string | OptionType, b: string | OptionType) => {
  if (typeof a === 'string' || typeof b === 'string') {
    return a === b;
  }
  // Can't safely compare non-string (ReactElement) labels — treat as changed.
  if (typeof a.label !== 'string' || typeof b.label !== 'string') {
    return false;
  }
  return (
    a.value === b.value && a.label === b.label && a.disabled === b.disabled
  );
};

// Whether two resolved selections are equivalent by id, label and disabled
// state. Used to reuse the previous value reference so MUI's Autocomplete does
// not mistake an unrelated re-render for a value change and reset typed text.
const isSameSelection = (
  prev: (string | OptionType)[] | OptionType | null,
  next: (string | OptionType)[] | OptionType | null,
) => {
  if (prev === next) {
    return true;
  }
  if (prev === null || next === null) {
    return false;
  }
  const prevIsArray = Array.isArray(prev);
  const nextIsArray = Array.isArray(next);
  if (prevIsArray !== nextIsArray) {
    return false;
  }
  if (prevIsArray && nextIsArray) {
    if (prev.length !== next.length) {
      return false;
    }
    return prev.every((item, index) => isSameOption(item, next[index]));
  }
  return isSameOption(prev as string | OptionType, next as string | OptionType);
};

export interface BaseAutocompleteProps
  extends Omit<InputBaseProps, 'value' | 'error' | 'onChange' | 'onKeyDown'>,
    BaseFieldProps {
  value: string | string[];
  emptyContent?: React.ReactNode;
  fixedValues?: string | string[];
  defaultValue?: OptionType;
  multiple?: boolean;
  limitTags?: number;
  items?: OptionType[];
  showPopupIcon?: true | false | 'auto';
  onChange?: (...event: any[]) => void;
  onChipDelete?: (value: string | number) => void;
  disableChipDelete?: boolean;
  onInputChange?: (name: string) => void;
  groupBy?: (option: any) => string;
  clearOnBlur?: boolean;
  freeSolo?: boolean;
  addNew?: boolean;
  onKeyDown?: (
    event: React.KeyboardEvent<HTMLDivElement> & {
      defaultMuiPrevented?: boolean;
    },
  ) => void;
  onCreate?: (newValue: string) => void;
  disableClearable?: boolean;
  disablePortal?: boolean;
}

export const BaseAutocomplete: FC<BaseAutocompleteProps> = ({
  value,
  fixedValues,
  label,
  multiple,
  limitTags,
  items = [],
  onChange,
  onInputChange,
  error,
  disabled = false,
  groupBy,
  placeholder,
  showPopupIcon,
  sx,
  clearOnBlur = true,
  freeSolo = false,
  addNew = true,
  onKeyDown,
  onCreate,
  disableClearable,
  emptyContent,
  onChipDelete,
  disableChipDelete = false,
  disablePortal,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState<number | null>(null);
  const [reservedSpace, setReservedSpace] = useState<number>(48);
  const tagsContainerRef = useRef<HTMLSpanElement | null>(null);
  const measureContainerRef = useRef<HTMLSpanElement | null>(null);
  const endAdornmentRef = useRef<HTMLSpanElement | null>(null);
  const getValueRef = useRef<(string | OptionType)[] | OptionType | null>(null);
  const stableValueRef = useRef<OptionType[] | OptionType | null>(null);

  const isIncludingGroupOption = useMemo(() => {
    return (
      !!items.length &&
      typeof items[0] !== 'string' &&
      items.findIndex(({ group }) => group) !== -1
    );
  }, [items]);

  const processedItems = useMemo(() => {
    if (!isIncludingGroupOption) {
      return items;
    }

    const newItems = [];
    let group = '';
    for (const item of items) {
      if (item.group) {
        group = item.group;
      } else {
        newItems.push({
          ...item,
          group,
        });
      }
    }
    return newItems;
  }, [items]);

  const renderOption = (props: any, option: string | OptionType) => {
    let listText: string | ReactElement;
    let optionKey: string;

    if (typeof option === 'string') {
      listText = option;
      optionKey = `list-item-${listText}`;
    } else if (typeof option.label === 'string') {
      listText = option.label;
      optionKey = `list-item-${option.value}`;
    } else {
      // If it's a ReactElement (like our "Create new" option), render it directly
      listText = option.label;
      optionKey = `list-item-${option.value}`;
    }

    return (
      <li
        {...props}
        key={optionKey}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        }}
        title={typeof listText === 'string' ? listText : ''}
      >
        {listText}
      </li>
    );
  };

  const groupByGroupOption = (option: any) => option.group;

  const handleChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string | OptionType | (string | OptionType)[] | null,
      reason: string,
    ) => {
      if (onChange) {
        if (reason === 'clear') {
          const currentSelected = getValueRef.current;
          const disabledIds = Array.isArray(currentSelected)
            ? currentSelected
                .filter(
                  (opt): opt is OptionType =>
                    typeof opt !== 'string' && Boolean(opt.disabled),
                )
                .map((opt) => opt.value)
            : [];
          const fixed = fixedValues
            ? Array.isArray(fixedValues)
              ? fixedValues
              : [fixedValues]
            : [];
          const toKeep = [...new Set([...fixed, ...disabledIds.map(String)])];
          if (toKeep.length > 0) {
            onChange(multiple ? toKeep : toKeep[0] ?? null);
          } else {
            onChange(null);
          }
          return;
        }
        if (typeof newValue === 'string') {
          onChange(newValue);
        } else if (!Array.isArray(newValue)) {
          if (
            typeof newValue === 'object' &&
            newValue !== null &&
            'inputValue' in newValue
          ) {
            if (onCreate && newValue.inputValue) {
              onCreate(newValue.inputValue);
            }
            onChange(newValue.value);
          } else {
            onChange(newValue?.value);
          }
        } else {
          const createValues = newValue
            .filter(
              (v) =>
                typeof v === 'object' &&
                v !== null &&
                'inputValue' in v &&
                Boolean(v.inputValue),
            )
            .map((v) => (v as OptionType).inputValue as string);
          if (onCreate && createValues.length) {
            createValues.forEach((createdValue) => onCreate(createdValue));
          }
          const valueArr = newValue.map((v) =>
            typeof v === 'string' ? v : v.value,
          );

          if (reason === 'removeOption') {
            const currentSelected = getValueRef.current;
            const disabledIds = Array.isArray(currentSelected)
              ? currentSelected
                  .filter(
                    (opt): opt is OptionType =>
                      typeof opt !== 'string' && Boolean(opt.disabled),
                  )
                  .map((opt) => String(opt.value))
              : [];
            const merged = [
              ...new Set([...valueArr.map(String), ...disabledIds]),
            ];
            onChange(merged);
            return;
          }

          onChange(valueArr);
        }
      }
    },
    [onChange, onCreate],
  );

  const getValue = useMemo(() => {
    let next: OptionType[] | OptionType | null;
    if (value === '') {
      next = multiple ? [] : null;
    } else if (typeof value === 'string' || typeof value === 'number') {
      next = items?.find((option) => option.value === value) || null;
    } else if (Array.isArray(value)) {
      const values: OptionType[] = [];
      value.forEach((item) => {
        const optionValue = items.find((option) => option.value === item);
        if (optionValue) values.push(optionValue);
      });
      next = values;
    } else {
      next = multiple ? [] : null;
    }

    // Reuse the previous reference when the resolved selection is unchanged so
    // MUI's Autocomplete does not treat an unrelated re-render (e.g. options
    // rebuilt with a new array reference) as a value change, which would reset
    // the user's in-progress typed text.
    const prev = stableValueRef.current;
    if (isSameSelection(prev, next)) {
      return prev;
    }
    stableValueRef.current = next;
    return next;
  }, [value, items, multiple]);

  getValueRef.current = getValue;

  const selectedValues = useMemo(() => {
    if (Array.isArray(getValue)) {
      return getValue;
    }
    return getValue ? [getValue] : [];
  }, [getValue]);

  const shouldCollapseTags = Boolean(multiple && typeof limitTags === 'number');

  const getOptionLabel = useCallback((option: string | OptionType) => {
    // Value selected with enter, right from the input
    if (!option || typeof option === 'string') {
      return option || '';
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return (option.label as string) || '';
  }, []);

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      name: string,
      reason: 'input' | 'reset' | 'clear',
    ) => {
      if (reason === 'reset' && !name && !multiple) {
        return;
      }

      const nextInputValue = reason === 'clear' ? '' : name;
      setInputValue(nextInputValue);

      if (onInputChange) {
        onInputChange(nextInputValue);
      }
    },
    [onInputChange, multiple],
  );

  const filterOptions = useCallback(
    (
      options: (string | OptionType)[],
      params: FilterOptionsState<string | OptionType>,
    ) => {
      const filtered = filter(options, params);
      const { inputValue: newInputValue } = params;

      if (
        freeSolo &&
        newInputValue !== '' &&
        addNew &&
        Array.isArray(options) &&
        !options.some((option) => {
          if (typeof option === 'string') {
            return false;
          }
          return option.label === newInputValue;
        })
      ) {
        filtered.push({
          inputValue: newInputValue,
          label: (
            <Typography
              color="error"
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                textAlign: 'left',
                width: '100%',
                flexWrap: 'wrap',
              }}
            >
              <IcPlusCircle style={{ marginRight: 5, marginTop: '2px' }} />
              Create{' '}
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  mx: 0.5,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-all',
                  whiteSpace: 'normal',
                  display: 'inline-block',
                }}
              >
                &quot;{newInputValue}&quot;
              </Typography>{' '}
              as a new item
            </Typography>
          ),
          value: newInputValue,
        });
      }

      return filtered;
    },
    [freeSolo],
  );

  const isDisabled = (option: string | OptionType) => {
    const valueToCheck = typeof option === 'string' ? option : option.value;

    if (fixedValues?.includes(valueToCheck.toString())) {
      return true;
    }
    if (typeof option === 'string') {
      return false;
    }
    return option.disabled || false;
  };

  const calculateVisibleTags = useCallback(() => {
    if (!shouldCollapseTags || isPopupOpen) {
      setVisibleTagCount(null);
      return;
    }

    const containerWidth =
      tagsContainerRef.current?.getBoundingClientRect().width || 0;
    if (!containerWidth || !measureContainerRef.current) {
      setVisibleTagCount(null);
      return;
    }

    const endAdornmentWidth =
      endAdornmentRef.current?.getBoundingClientRect().width || 0;
    const nextReservedSpace = Math.max(48, Math.ceil(endAdornmentWidth + 8));
    setReservedSpace((prev) =>
      prev === nextReservedSpace ? prev : nextReservedSpace,
    );

    const availableWidth = containerWidth - nextReservedSpace;
    const chipGap = 6;

    const chipNodes = Array.from(
      measureContainerRef.current.querySelectorAll('[data-chip]'),
    ) as HTMLElement[];
    const moreNode = measureContainerRef.current.querySelector(
      '[data-more]',
    ) as HTMLElement | null;
    const tagWidths = chipNodes.map(
      (el) => el.getBoundingClientRect().width || 0,
    );
    const moreChipWidth = moreNode?.getBoundingClientRect().width || 32;

    let usedWidth = 0;
    let count = 0;
    for (const width of tagWidths) {
      const nextWidth = usedWidth + width + (count > 0 ? chipGap : 0);
      if (nextWidth <= availableWidth) {
        usedWidth = nextWidth;
        count += 1;
      } else {
        break;
      }
    }

    if (count < tagWidths.length) {
      usedWidth = 0;
      count = 0;
      for (const width of tagWidths) {
        const nextWidth = usedWidth + width + (count > 0 ? chipGap : 0);
        if (nextWidth + moreChipWidth + chipGap <= availableWidth) {
          usedWidth = nextWidth;
          count += 1;
        } else {
          break;
        }
      }
    }

    // Always show at least the first tag (truncated if needed)
    if (count === 0 && tagWidths.length > 0) {
      count = 1;
    }

    setVisibleTagCount(count);
  }, [shouldCollapseTags, isPopupOpen]);

  useLayoutEffect(() => {
    calculateVisibleTags();
  }, [calculateVisibleTags, selectedValues.length]);

  useLayoutEffect(() => {
    const inputRoot =
      tagsContainerRef.current?.closest('.MuiAutocomplete-inputRoot') ||
      tagsContainerRef.current?.closest('.MuiInputBase-root') ||
      tagsContainerRef.current?.parentElement;
    if (!inputRoot) return;

    const observer = new ResizeObserver(() => {
      calculateVisibleTags();
    });
    observer.observe(inputRoot);

    return () => observer.disconnect();
  }, [calculateVisibleTags]);

  return (
    <AutoCompleteRoot
      sx={sx}
      className={isPopupOpen ? 'autocomplete-open' : 'autocomplete-closed'}
    >
      {!!label && <InputLabel>{label}</InputLabel>}
      <Autocomplete
        disablePortal={disablePortal}
        autoHighlight
        value={getValue}
        disableClearable={disableClearable || !getValue}
        disabled={disabled}
        inputValue={inputValue}
        onChange={handleChange}
        groupBy={isIncludingGroupOption ? groupByGroupOption : groupBy}
        onInputChange={handleInputChange}
        options={processedItems}
        getOptionLabel={getOptionLabel}
        noOptionsText={<NoResultItem />}
        renderOption={renderOption}
        getLimitTagsText={(more) => (
          <div
            style={{
              marginTop: -4,
              position: 'absolute',
              right: '25px',
              top: '6px',
            }}
          >
            <Tooltip
              arrow
              title={(getValue as OptionType[])?.map((v: any) => (
                <b key={v.value}>
                  {v.label}
                  <br />
                </b>
              ))}
            >
              <Chip variant="filled" color="error" label={`${more}+`}></Chip>
            </Tooltip>
          </div>
        )}
        getOptionDisabled={(option) =>
          Boolean((option as OptionType)?.disabled)
        }
        renderTags={(values: (string | OptionType)[], getTagProps) => {
          const collapseActive = shouldCollapseTags && !isPopupOpen;
          const fallbackCount =
            typeof limitTags === 'number' ? limitTags : values.length;
          const allowedCount = collapseActive
            ? Math.max(
                0,
                Math.min(values.length, visibleTagCount ?? fallbackCount),
              )
            : values.length;
          const visibleTags = collapseActive
            ? values.slice(0, allowedCount)
            : values;
          const hiddenCount = values.length - visibleTags.length;

          return (
            <>
              <span
                ref={measureContainerRef}
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}
              >
                {values.map((option: string | OptionType, index: number) => (
                  <Chip
                    key={`measure-${index}`}
                    variant="filled"
                    color="error"
                    label={typeof option === 'string' ? option : option.label}
                    sx={{ flexShrink: 0, maxWidth: 'none' }}
                    data-chip
                    disabled={isDisabled(option)}
                  ></Chip>
                ))}
                <Chip
                  variant="filled"
                  color="error"
                  label="99+"
                  sx={{ flexShrink: 0, maxWidth: 'none' }}
                  data-more
                ></Chip>
              </span>
              <span
                ref={tagsContainerRef}
                style={{
                  display: 'flex',
                  flexWrap: collapseActive ? 'nowrap' : 'wrap',
                  overflow: 'visible',
                  gap: 6,
                  boxSizing: 'border-box',
                  paddingRight: collapseActive ? reservedSpace : 0,
                  maxWidth: '100%',
                }}
              >
                {visibleTags.map(
                  (option: string | OptionType, index: number) => (
                    <Chip
                      variant="filled"
                      color="error"
                      label={typeof option === 'string' ? option : option.label}
                      sx={{
                        flexShrink: collapseActive ? 1 : 0,
                        maxWidth: collapseActive ? '100%' : 'none',
                        '& .MuiChip-label': collapseActive
                          ? {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }
                          : {},
                      }}
                      {...getTagProps({ index })}
                      key={index}
                      {...(disabled || disableChipDelete
                        ? { onDelete: undefined }
                        : onChipDelete
                        ? {
                            onDelete: (e) => {
                              e.stopPropagation();
                              e.preventDefault();

                              onChipDelete(
                                typeof option === 'string'
                                  ? option
                                  : option.value,
                              );
                            },
                          }
                        : undefined)}
                      deleteIcon={
                        <IcClose color="white" style={{ minWidth: '20px' }} />
                      }
                      disabled={isDisabled(option)}
                    ></Chip>
                  ),
                )}
                {hiddenCount > 0 && (
                  <Tooltip
                    arrow
                    title={values.map((option) => {
                      const labelValue =
                        typeof option === 'string' ? option : option.label;
                      return (
                        <b key={String(labelValue)}>
                          {labelValue}
                          <br />
                        </b>
                      );
                    })}
                  >
                    <Chip
                      variant="filled"
                      color="error"
                      label={`${hiddenCount}+`}
                      sx={{ flexShrink: 0, maxWidth: 'none' }}
                    />
                  </Tooltip>
                )}
              </span>
            </>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            inputProps={{
              ...params.inputProps,
              ...(multiple && !isPopupOpen
                ? {
                    style: {
                      ...((params.inputProps as any)?.style || {}),
                      width: 0,
                      minWidth: 0,
                      height: 0,
                      padding: 0,
                      opacity: 0,
                      overflow: 'hidden' as const,
                    },
                  }
                : {}),
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <span ref={endAdornmentRef}>
                  {params.InputProps.endAdornment}
                </span>
              ),
              startAdornment:
                multiple &&
                Array.isArray(getValue) &&
                getValue.length === 0 &&
                emptyContent ? (
                  <>{emptyContent}</>
                ) : (
                  params.InputProps.startAdornment
                ),
            }}
          />
        )}
        forcePopupIcon={showPopupIcon}
        popupIcon={<IcArrowDown />}
        multiple={multiple}
        limitTags={shouldCollapseTags ? -1 : limitTags}
        handleHomeEndKeys
        filterSelectedOptions
        onOpen={() => setIsPopupOpen(true)}
        onClose={() => setIsPopupOpen(false)}
        openOnFocus
        selectOnFocus
        clearOnBlur={clearOnBlur}
        onKeyDown={onKeyDown}
        freeSolo={freeSolo}
        filterOptions={filterOptions}
      />
      <FormHelperText error>{error}</FormHelperText>
    </AutoCompleteRoot>
  );
};
